import { inject, Injectable, TemplateRef } from '@angular/core';
import { captureException } from '@sentry/angular';
import { WilsonState } from '@wilson/non-domain-specific/decorators/wilson-state';
import { NzMessageService } from 'ng-zorro-antd/message';
import { map, Observable, shareReplay } from 'rxjs';
import { GroupItemsSelectionHelperService } from './group-items-selection-helper.service';
import { ShiftDrawerCloseHelperService } from './shift-drawer-close-helper.service';

export interface ShiftIdWithPosition {
  id: string;
  left: number | null;
  right: number | null;
  isUnAssignedShift: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class ShiftMultiSelectHelperService {
  private readonly nzMessageService = inject(NzMessageService);
  private readonly shiftDrawerCloseHelperService = inject(
    ShiftDrawerCloseHelperService,
  );
  private readonly groupItemsSelectionHelperService = inject(
    GroupItemsSelectionHelperService,
  );

  @WilsonState<ShiftIdWithPosition[]>([])
  private selectedShifts!: ShiftIdWithPosition[];
  public readonly selectedShifts$!: Observable<ShiftIdWithPosition[]>;

  public readonly selectedShiftIds$ = this.selectedShifts$.pipe(
    map((shifts) => shifts.map((shift) => shift.id)),
  );

  public readonly singleSelectedShiftIdWithPosition$: Observable<ShiftIdWithPosition | null> =
    this.selectedShifts$.pipe(
      map((shiftIdWithPositions) => {
        if (shiftIdWithPositions.length === 1) {
          return shiftIdWithPositions[0];
        } else {
          return null;
        }
      }),
      shareReplay(1),
    );

  public isSingleSelectedShiftUnassigned$: Observable<boolean> =
    this.singleSelectedShiftIdWithPosition$.pipe(
      map(
        (shiftIdWithPosition) =>
          shiftIdWithPosition?.isUnAssignedShift ?? false,
      ),
      shareReplay(1),
    );

  private previouslySelectedShifts: ShiftIdWithPosition[] = [];

  public async handleClick({
    shiftId,
    event,
    leftPixelPosition,
    rightPixelPosition,
    singleShiftSelectedCallback,
    templateRef,
    messageDuration,
    isUnAssignedShift,
  }: {
    shiftId: string;
    event: MouseEvent;
    leftPixelPosition: number | null;
    rightPixelPosition: number | null;
    singleShiftSelectedCallback: (shift: ShiftIdWithPosition) => void;
    templateRef: TemplateRef<void>;
    messageDuration: number;
    isUnAssignedShift: boolean;
  }): Promise<void> {
    const isShiftKeySelection = event.shiftKey;
    const selectedShifts = this.selectedShifts;
    const isShiftAlreadySelected = selectedShifts.some(
      (shift) => shift.id === shiftId,
    );
    const isOnlyOneShiftSelected = selectedShifts.length === 1;
    const isNoShiftSelected = selectedShifts.length === 0;
    const hasMultipleShiftSelected = selectedShifts.length > 1;
    const isDeselectingSingleShift =
      !isShiftKeySelection && isShiftAlreadySelected && isOnlyOneShiftSelected;

    if (isShiftKeySelection) {
      await this.handleShiftKeySelection({
        isOnlyOneShiftSelected,
        shiftId,
        leftPixelPosition,
        rightPixelPosition,
        isUnAssignedShift,
      });
    } else {
      await this.handleRegularSelection({
        shiftId,
        leftPixelPosition,
        rightPixelPosition,
        isUnAssignedShift,
        isNoShiftSelected,
        isShiftAlreadySelected,
        isOnlyOneShiftSelected,
        hasMultipleShiftSelected,
        templateRef,
        messageDuration,
      });
    }

    const updatedSelectedShifts = this.selectedShifts;
    if (updatedSelectedShifts.length === 1) {
      const selectedShiftId = updatedSelectedShifts[0].id;
      const shiftWithPosition = this.getShiftPosition(selectedShiftId);
      singleShiftSelectedCallback({
        id: selectedShiftId,
        left: shiftWithPosition?.left || null,
        right: shiftWithPosition?.right || null,
        isUnAssignedShift,
      });
    }

    const isShiftSelected = this.selectedShifts.some(
      (shift) => shift.id === shiftId,
    );

    if (isDeselectingSingleShift) {
      await this.groupItemsSelectionHelperService.clearSelectedItems();
    } else {
      const activeElementIds = this.selectedShifts.map((shift) => shift.id);

      if (!isShiftSelected) {
        if (isShiftKeySelection) {
          await this.groupItemsSelectionHelperService.syncGroupItemsFromElementIds(
            activeElementIds,
          );
        } else {
          await this.groupItemsSelectionHelperService.clearSelectedItems();
        }
      } else if (isShiftKeySelection) {
        await this.groupItemsSelectionHelperService.updateGroupItemsSelection(
          shiftId,
        );
      } else {
        await this.groupItemsSelectionHelperService.selectGroupItems(shiftId);
      }
    }
  }

  private async handleRegularSelection({
    shiftId,
    leftPixelPosition,
    rightPixelPosition,
    isUnAssignedShift,
    isNoShiftSelected,
    isShiftAlreadySelected,
    isOnlyOneShiftSelected,
    hasMultipleShiftSelected,
    templateRef,
    messageDuration,
  }: {
    shiftId: string;
    leftPixelPosition: number | null;
    rightPixelPosition: number | null;
    isUnAssignedShift: boolean;
    isNoShiftSelected: boolean;
    isShiftAlreadySelected: boolean;
    isOnlyOneShiftSelected: boolean;
    hasMultipleShiftSelected: boolean;
    templateRef: TemplateRef<void>;
    messageDuration: number;
  }): Promise<void> {
    if (isNoShiftSelected) {
      this.setSelectedShift([
        {
          id: shiftId,
          left: leftPixelPosition,
          right: rightPixelPosition,
          isUnAssignedShift,
        },
      ]);
    } else if (isShiftAlreadySelected && isOnlyOneShiftSelected) {
      await this.shiftDrawerCloseHelperService.checkPristineAndCloseDrawer({
        onOkCallback: () => {
          this.clearSelection();
        },
      });
    } else if (hasMultipleShiftSelected) {
      this.clearSelectionWithUndo(templateRef, messageDuration);
      this.setSelectedShift([
        {
          id: shiftId,
          left: leftPixelPosition,
          right: rightPixelPosition,
          isUnAssignedShift,
        },
      ]);
    } else {
      // This case is for replacing the previous selected shift
      await this.shiftDrawerCloseHelperService.checkPristineAndCloseDrawer({
        onOkCallback: () => {
          this.setSelectedShift([
            {
              id: shiftId,
              left: leftPixelPosition,
              right: rightPixelPosition,
              isUnAssignedShift,
            },
          ]);
        },
      });
    }
  }

  private async handleShiftKeySelection({
    isOnlyOneShiftSelected,
    shiftId,
    leftPixelPosition,
    rightPixelPosition,
    isUnAssignedShift,
  }: {
    isOnlyOneShiftSelected: boolean;
    shiftId: string;
    leftPixelPosition: number | null;
    rightPixelPosition: number | null;
    isUnAssignedShift: boolean;
  }): Promise<void> {
    if (isOnlyOneShiftSelected) {
      await this.shiftDrawerCloseHelperService.checkPristineAndCloseDrawer({
        onOkCallback: () => {
          this.selectOrUnselectShift({
            id: shiftId,
            left: leftPixelPosition,
            right: rightPixelPosition,
            isUnAssignedShift,
          });
        },
      });
    } else {
      this.selectOrUnselectShift({
        id: shiftId,
        left: leftPixelPosition,
        right: rightPixelPosition,
        isUnAssignedShift,
      });
    }
  }

  public async unselect(shiftId: string): Promise<void> {
    try {
      const selectedShifts = this.selectedShifts;
      const shiftIndex = selectedShifts.findIndex(
        (shift) => shift.id === shiftId,
      );

      if (shiftIndex > -1) {
        selectedShifts.splice(shiftIndex, 1);
      }

      this.selectedShifts = selectedShifts;
      await this.groupItemsSelectionHelperService.syncGroupItemsFromElementIds(
        this.selectedShifts.map((shift) => shift.id),
      );
    } catch (error) {
      captureException(error);
    }
  }

  public clearSelection(): void {
    this.selectedShifts = [];
    this.groupItemsSelectionHelperService.clearSelectedItems();
  }

  public clearSelectionWithUndo(
    templateRef: TemplateRef<void>,
    messageDuration: number,
  ): void {
    this.previouslySelectedShifts = this.selectedShifts;
    this.clearSelection();
    if (this.previouslySelectedShifts.length > 1) {
      this.nzMessageService.warning(templateRef, {
        nzDuration: messageDuration,
        nzPauseOnHover: true,
      });
    }
  }

  public async undoSelection(): Promise<void> {
    try {
      this.setSelectedShift(this.previouslySelectedShifts);
      await this.groupItemsSelectionHelperService.syncGroupItemsFromElementIds(
        this.previouslySelectedShifts.map((shift) => shift.id),
      );
      this.nzMessageService.remove();
    } catch (error) {
      captureException(error);
    }
  }

  public setSelectedShift(shiftsWithPosition: ShiftIdWithPosition[]): void {
    this.selectedShifts = shiftsWithPosition;
  }

  private getShiftPosition(shiftId: string): ShiftIdWithPosition | undefined {
    const selectedShifts = this.selectedShifts;
    return selectedShifts.find((shift) => shift.id === shiftId);
  }

  private selectOrUnselectShift(shiftWithPosition: ShiftIdWithPosition): void {
    const selectedShifts = this.selectedShifts;
    const shiftIndex = selectedShifts.findIndex(
      (shift) => shift.id === shiftWithPosition.id,
    );

    if (shiftIndex === -1) {
      selectedShifts.push(shiftWithPosition);
    } else {
      selectedShifts.splice(shiftIndex, 1);
    }

    this.selectedShifts = selectedShifts;
  }
}
